// Template library page component
// CHANGE: Added printInProgress ref guard to prevent concurrent PDF generation across templates
// CHANGE: Removed all debug console statements for production
// Main page for managing task templates
// CHANGE: Created for Phase 2 - Template library
// CHANGE: Updated to match SiteManagement/UserManagement table layout
// CHANGE: Added recurrence display in Phase 2 - Task 3
// CHANGE: Harmonized design with UnifiedView: reduced title size, improved spacing, modern shadows
// CHANGE: Added sidebar toggle button for collapsible sidebar feature
// CHANGE: Added search bar for better template discovery (client-side filtering)
// CHANGE: Removed filter dropdowns, keeping only search functionality
// CHANGE: Moved "Voir les attributions" action to dropdown menu for better UX consistency
// CHANGE: Removed ATTRIBUTIONS column to optimize page load performance - count now only loads in assignments modal
// UPDATED: Migrated to centralized toast system for all notifications
// CHANGE: Added dedicated SERVICE column to display associated service for each mission
// CHANGE: Added read-only mode for director role - can view all templates but cannot create/modify/assign
// CHANGE: Harmonized interface with UserManagement page (December 2025):
// UPDATED: Mobile header now has white background with bottom border separator matching bottom nav styling
//   - Simplified titles: "Missions" instead of "Listes de tâches", "Missions archivées" instead of "Listes de tâches archivées"
//   - Removed descriptive paragraph under title
//   - Added separate "Archivées" button in header (like "Désactivés" in UserManagement)
//   - Added item counter in main header and archived header with smart display (X sur Y when filtering)
//   - Unified button sizes and styles (px-5 py-2.5, icon size 18px)
//   - Streamlined search bar: removed white wrapper, integrated directly in flex layout
//   - Optimized actions menu: added group class and hover-to-show behavior (opacity-0 to opacity-100)
//   - Cleaned up code: removed handleClearSearch function, inline reset functionality
//   - Consistent spacing and layout structure across both pages
// CHANGE: UI optimization following "less is more" principle (December 2025):
//   - Simplified table to 5 columns: Nom, Service, Tâches, Durée, Actions (removed Récurrence column)
//   - Removed decorative icons (FileText, Clock) from cells for cleaner look
//   - Added Repeat icon next to mission name for missions with recurrence settings
//   - Recurrence details shown in tooltip on hover over Repeat icon
//   - Simplified sorting: removed created_at and updated_at sort options, kept only name sorting
//   - Reduced visual noise while maintaining essential information accessibility
// UPDATED: Replaced native select with FilterDropdown for service filtering (harmonized with UserManagement style)
// CHANGE: Added mobile responsive card view with ResponsiveDataView and ResponsiveDataCard
// CHANGE: Added ReadOnlyBanner to inform users they are in consultation mode on mobile
// UPDATED: Implemented server-side pagination with infinite scroll for better performance
// UPDATED: Added MobileSearchFilters and MobileFilterBottomSheet for mobile-optimized filtering
// UPDATED: Added skeleton loading states for improved perceived performance
// FIX: Added data transformation for itemCount and recurrence from raw Supabase response
// UPDATED: handleRefresh now reloads services to ensure color changes are reflected after modifications
// REFACTOR: Replaced card view with table view (MissionTable) with sortable column headers
// CHANGE: Implemented multi-column sorting (Mission, Service, Duration) with clickable headers
// CHANGE: Sort button removed from control bar - sorting now integrated in table headers
// CHANGE: Table displays on all screen sizes including mobile with expandable mission details
// REFACTOR: Moved direct supabase.from() calls to service modules (getServices, getActiveUsers, getActiveSites)
// CHANGE: Added "Télécharger PDF" action to mission dropdown menu for all user roles, using missionDetailsPdf.ts

import { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Archive, ArchiveRestore, Copy, Pencil, ArrowLeft, UserPlus, Upload, Eye, Printer } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { usePaginatedData } from '../../hooks/usePaginatedData';
import { MissionModal } from './MissionModal';
import { MissionTable, type SortField } from './MissionTable';
import { AssignmentModal } from './AssignmentModal';
import { AssignmentsModal } from './AssignmentsModal';
import { TemplateImportModal } from './TemplateImportModal';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import type { DropdownMenuItem } from '../ui/DropdownMenu';
import { ConsolidatedControlBar } from '../ui/ConsolidatedControlBar';
import type { FilterOption } from '../ui/FilterDropdown';
import { MobileSearchFilters } from '../ui/MobileSearchFilters';
import { MobileFilterBottomSheet, type FilterGroup } from '../ui/MobileFilterBottomSheet';
import type { User, Site } from '../../lib/supabase';
import { supabase } from '../../lib/supabase';
import type { Service } from '../../lib/services';
import { getServices } from '../../lib/services';
import { getActiveSites } from '../../lib/sites';
import { getActiveUsers } from '../../lib/auth';
import {
  archiveTemplate,
  restoreTemplate,
  duplicateTemplate,
  getTemplateWithItems,
  type TemplateWithItems
} from '../../lib/templates';
import { generateMissionDetailsPdf } from '../../lib/missionDetailsPdf';
import { ReadOnlyBanner } from '../ui/ReadOnlyBanner';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { InfiniteScrollLoader } from '../ui/InfiniteScrollLoader';
import { BouncingDotsLoader } from '../ui/BouncingDotsLoader';

type SortOrder = 'asc' | 'desc';

export function TemplateLibrary() {
  const { user: currentUser } = useAuth();
  const { isMobile, isDesktop } = useResponsiveLayout();
  const [showImportModal, setShowImportModal] = useState(false);
  const [missionModalTemplate, setMissionModalTemplate] = useState<TemplateWithItems | null | 'new'>(null);
  const [showArchived, setShowArchived] = useState(false);
  const { showToast } = useToast();

  // Check if current user is director or on mobile (read-only mode)
  const isReadOnly = currentUser?.role === 'director' || isMobile;
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [archivingTemplate, setArchivingTemplate] = useState<TemplateWithItems | null>(null);
  const [restoringTemplate, setRestoringTemplate] = useState<TemplateWithItems | null>(null);
  const [assigningTemplate, setAssigningTemplate] = useState<TemplateWithItems | null>(null);
  const [viewingAssignments, setViewingAssignments] = useState<TemplateWithItems | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [expandedMissionId, setExpandedMissionId] = useState<string | null>(null);
  const [printingTemplateId, setPrintingTemplateId] = useState<string | null>(null);
  const printInProgress = useRef<boolean>(false);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Build filters object for server-side filtering
  const filters = useMemo(() => {
    return {
      is_archived: showArchived,
    };
  }, [showArchived]);

  // Use paginated data hook for templates
  // Note: We only use server-side ordering for 'name' and 'estimated_duration_minutes'
  // Service sorting is handled client-side since it's a related table
  const serverOrderColumn = sortField === 'service' ? 'name' : sortField;

  const {
    items: rawTemplates,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    refresh,
    totalCount,
  } = usePaginatedData<TemplateWithItems>({
    supabase,
    table: 'task_templates',
    pageSize: 20,
    orderBy: { column: serverOrderColumn, ascending: sortOrder === 'asc' },
    filters,
    searchColumn: 'name',
    searchTerm,
    select: '*, service:services(*), items:task_items(count), recurrence:recurrence_rules(*)',
  });

  const templates = useMemo(() => {
    const transformed = rawTemplates.map(t => ({
      ...t,
      itemCount: (t as any).items?.[0]?.count || 0,
      recurrence: (t as any).recurrence?.[0] || null,
    }));

    // Client-side sorting for service field
    if (sortField === 'service') {
      return transformed.sort((a, b) => {
        const aService = a.service?.name || '';
        const bService = b.service?.name || '';
        const comparison = aService.localeCompare(bService);
        return sortOrder === 'asc' ? comparison : -comparison;
      });
    }

    return transformed;
  }, [rawTemplates, sortField, sortOrder]);

  useEffect(() => {
    loadUsersAndSites();
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const result = await getServices();
      if (result.success && result.data) {
        setServices(result.data);
      }
    } catch (err) {
    }
  };

  const loadUsersAndSites = async () => {
    try {
      const [usersResult, sitesResult] = await Promise.all([
        getActiveUsers(),
        getActiveSites(),
      ]);

      if (usersResult.success && usersResult.data) {
        setUsers(usersResult.data);
      }
      if (sitesResult.success && sitesResult.data) {
        setSites(sitesResult.data);
      }
    } catch (err) {
    }
  };

  const handleRefresh = () => {
    loadServices();
    refresh();
  };

  const handleMissionSaved = () => {
    setMissionModalTemplate(null);
    handleRefresh();
  };

  const handleArchiveTemplate = async (template: TemplateWithItems) => {
    setArchivingTemplate(template);
  };

  const performArchive = async () => {
    if (!archivingTemplate) return;
    try {
      await archiveTemplate(archivingTemplate.id);
      setArchivingTemplate(null);
      showToast('Mission archivée avec succès', 'success');
      handleRefresh();
    } catch (err) {
      showToast('Erreur lors de l\'archivage de la mission', 'error');
      setArchivingTemplate(null);
    }
  };

  const handleRestoreTemplate = async (template: TemplateWithItems) => {
    setRestoringTemplate(template);
  };

  const performRestore = async () => {
    if (!restoringTemplate) return;
    try {
      await restoreTemplate(restoringTemplate.id);
      setRestoringTemplate(null);
      showToast('Mission restaurée avec succès', 'success');
      handleRefresh();
    } catch (err) {
      showToast('Erreur lors de la restauration de la mission', 'error');
      setRestoringTemplate(null);
    }
  };

  const handleDuplicateTemplate = async (template: TemplateWithItems) => {
    try {
      await duplicateTemplate(template.id);
      showToast('Mission dupliquée avec succès', 'success');
      handleRefresh();
    } catch (err) {
      showToast('Erreur lors de la duplication de la mission', 'error');
    }
  };

  const handlePrintTemplate = async (template: TemplateWithItems) => {
    if (printInProgress.current) return;
    printInProgress.current = true;
    setPrintingTemplateId(template.id);
    await new Promise<void>(resolve => setTimeout(resolve, 50));
    try {
      const full = await getTemplateWithItems(template.id);
      if (!full) {
        showToast('Impossible de charger les données de la mission', 'error');
        return;
      }
      await generateMissionDetailsPdf({
        name: full.name,
        description: full.description,
        estimated_duration_minutes: full.estimated_duration_minutes,
        service: full.service ?? null,
        items: full.items || [],
        recurrence: full.recurrence ?? null,
      });
    } catch (err) {
      showToast('Erreur lors de la génération du PDF', 'error');
    } finally {
      setPrintingTemplateId(null);
      printInProgress.current = false;
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getDropdownItems = (template: TemplateWithItems): DropdownMenuItem[] => {
    const isPrinting = printingTemplateId === template.id;
    const printItem: DropdownMenuItem = {
      label: isPrinting ? 'Génération...' : 'Télécharger PDF',
      icon: <Printer size={16} className={isPrinting ? 'animate-pulse' : ''} />,
      onClick: () => handlePrintTemplate(template),
      disabled: isPrinting || printInProgress.current,
    };

    if (isReadOnly) {
      return [printItem];
    }

    if (showArchived) {
      return [
        printItem,
        {
          label: 'Restaurer',
          icon: <ArchiveRestore size={16} />,
          onClick: () => handleRestoreTemplate(template),
        },
      ];
    }

    return [
      {
        label: 'Voir les attributions',
        icon: <Eye size={16} />,
        onClick: () => setViewingAssignments(template),
      },
      {
        label: 'Attribuer',
        icon: <UserPlus size={16} />,
        onClick: () => setAssigningTemplate(template),
        disabled: !template.itemCount || template.itemCount === 0,
      },
      printItem,
      {
        label: 'Modifier',
        icon: <Pencil size={16} />,
        onClick: () => {
          setMissionModalTemplate(template);
        },
      },
      {
        label: 'Dupliquer',
        icon: <Copy size={16} />,
        onClick: () => handleDuplicateTemplate(template),
      },
      {
        label: 'Archiver',
        icon: <Archive size={16} />,
        onClick: () => handleArchiveTemplate(template),
        variant: 'danger' as const,
      },
    ];
  };


  // Client-side service filtering
  const filteredTemplates = useMemo(() => {
    if (selectedServiceIds.length === 0) {
      return templates;
    }

    return templates.filter(template => {
      if (selectedServiceIds.includes('_none')) {
        // If "_none" is selected, also include templates without service
        if (!template.service_id) return true;
      }
      // Check if template's service is in the selected services
      return template.service_id && selectedServiceIds.includes(template.service_id);
    });
  }, [templates, selectedServiceIds]);

  const hasActiveSearch = searchTerm.length > 0 || selectedServiceIds.length > 0;

  // Prepare service filter options
  const serviceFilterOptions: FilterOption[] = useMemo(() => {
    const options: FilterOption[] = [
      { value: '_none', label: 'Sans service' }
    ];

    services.forEach(service => {
      options.push({
        value: service.id,
        label: service.name
      });
    });

    return options;
  }, [services]);


  // Mobile filter groups configuration
  const mobileFilterGroups: FilterGroup[] = useMemo(() => {
    return [
      {
        id: 'services',
        label: 'Services',
        options: serviceFilterOptions.map(opt => ({
          value: opt.value,
          label: opt.label,
        })),
        selectedValues: selectedServiceIds,
      }
    ];
  }, [serviceFilterOptions, selectedServiceIds]);

  const handleMobileFilterChange = (groupId: string, values: string[]) => {
    if (groupId === 'services') {
      setSelectedServiceIds(values);
    }
  };

  const handleClearAllMobileFilters = () => {
    setSelectedServiceIds([]);
    setSearchTerm('');
  };

  return (
    <div className="space-y-6">
      {/* Read-only banner for mobile */}
      {isMobile && <ReadOnlyBanner />}

      {/* Header */}
      {showArchived ? (
        <div className={isMobile ? 'bg-white border-b border-gray-200 -mx-4 px-4 pb-4' : ''}>
          <button
            onClick={() => setShowArchived(false)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors mb-4"
          >
            <ArrowLeft size={16} />
            <span>Retour</span>
          </button>
          <PageHeader title="Missions archivées" />
        </div>
      ) : (
        <PageHeader title="Missions" isMobile={isMobile}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowArchived(true)}
              className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-medium px-4 py-2.5 rounded-lg transition-all duration-200 border border-gray-200 shadow-sm hover:shadow-md"
            >
              <Archive size={18} />
              <span>Archivées</span>
            </button>
            {!isReadOnly && (
              <>
                <button
                  onClick={() => setShowImportModal(true)}
                  className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <Upload size={18} />
                  <span>Importer</span>
                </button>
                <button
                  onClick={() => setMissionModalTemplate('new')}
                  className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <Plus size={18} />
                  <span>Créer</span>
                </button>
              </>
            )}
          </div>
        </PageHeader>
      )}

      {/* Search and Filters */}
      {!showArchived && (
        <>
          {isMobile ? (
            <MobileSearchFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Rechercher une mission..."
              activeFilterCount={selectedServiceIds.length}
              onFilterClick={() => setShowMobileFilters(true)}
              showFilterButton={true}
            />
          ) : (
            <ConsolidatedControlBar
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Rechercher une mission..."
              itemCount={selectedServiceIds.length > 0 ? filteredTemplates.length : totalCount}
              itemLabel="mission"
              filters={[
                {
                  label: 'Services',
                  options: serviceFilterOptions,
                  selectedValues: selectedServiceIds,
                  onChange: setSelectedServiceIds,
                },
              ]}
              showSort={false}
            />
          )}
        </>
      )}

      {/* Mission table */}
      {filteredTemplates.length === 0 && !isLoading ? (
        <EmptyState
          message={showArchived ? 'Aucune mission archivée' : hasActiveSearch ? 'Aucune mission ne correspond à vos critères' : 'Aucune mission trouvée'}
          actionLabel={hasActiveSearch && !showArchived ? 'Réinitialiser les filtres' : undefined}
          onAction={hasActiveSearch && !showArchived ? () => { setSearchTerm(''); setSelectedServiceIds([]); } : undefined}
        />
      ) : (
        <>
          {/* Loading state */}
          {isLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <BouncingDotsLoader size="lg" />
            </div>
          ) : (
            <MissionTable
              templates={filteredTemplates}
              expandedMissionId={expandedMissionId}
              onToggleExpand={(id) => {
                setExpandedMissionId(expandedMissionId === id ? null : id);
              }}
              getDropdownItems={getDropdownItems}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
          )}

          {/* Infinite scroll loader */}
          {!isLoading && (
            <InfiniteScrollLoader
              isLoading={isLoadingMore}
              hasMore={hasMore}
              onLoadMore={loadMore}
            />
          )}
        </>
      )}

      {/* Mission modal (create or edit) */}
      {missionModalTemplate && (
        <MissionModal
          template={missionModalTemplate === 'new' ? null : missionModalTemplate}
          onClose={() => setMissionModalTemplate(null)}
          onSaved={handleMissionSaved}
        />
      )}

      {/* Assignment modal */}
      {assigningTemplate && (
        <AssignmentModal
          template={assigningTemplate}
          users={users}
          sites={sites}
          onClose={() => setAssigningTemplate(null)}
          onAssigned={() => {
            setAssigningTemplate(null);
          }}
        />
      )}

      {/* Archive Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!archivingTemplate}
        title="Confirmer l'archivage"
        message={`Êtes-vous sûr de vouloir archiver la mission "${archivingTemplate?.name}" ? Elle sera déplacée dans les missions archivées et ne sera plus accessible aux utilisateurs.`}
        confirmLabel="Confirmer"
        cancelLabel="Annuler"
        onConfirm={performArchive}
        onCancel={() => setArchivingTemplate(null)}
        variant="warning"
      />

      {/* Restore Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!restoringTemplate}
        title="Confirmer la restauration"
        message={`Êtes-vous sûr de vouloir restaurer la mission "${restoringTemplate?.name}" ? Elle sera réactivée et à nouveau accessible aux utilisateurs.`}
        confirmLabel="Confirmer"
        cancelLabel="Annuler"
        onConfirm={performRestore}
        onCancel={() => setRestoringTemplate(null)}
        variant="info"
      />

      {/* Assignments View Modal */}
      {viewingAssignments && (
        <AssignmentsModal
          template={viewingAssignments}
          onClose={() => setViewingAssignments(null)}
        />
      )}

      {/* Template Import Modal */}
      {showImportModal && (
        <TemplateImportModal
          onClose={() => setShowImportModal(false)}
          onImportComplete={() => {
            setShowImportModal(false);
            handleRefresh();
          }}
        />
      )}

      {/* Mobile filter bottom sheet */}
      <MobileFilterBottomSheet
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        filterGroups={mobileFilterGroups}
        onChange={handleMobileFilterChange}
        onClearAll={handleClearAllMobileFilters}
      />
    </div>
  );
}
